import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuinielaStore } from '../../store/quinielaStore'
import { useAuthStore } from '../../store/authStore'
import { 
  PlusCircle, 
  Trophy, 
  Users, 
  DollarSign, 
  Calendar,
  Loader,
  FileText,
  TrendingUp,
  Edit,
  Eye,
  BarChart3
} from 'lucide-react'

export default function OperatorPanel() {
  const { user } = useAuthStore()
  const { quinielas, loading, fetchQuinielas } = useQuinielaStore()
  const [stats, setStats] = useState({
    totalQuinielas: 0,
    activeQuinielas: 0,
    totalParticipants: 0,
    totalRevenue: 0
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    const allQuinielas = await fetchQuinielas()
    
    // Calcular estadísticas
    const activeQuinielas = allQuinielas.filter(q => 
      q.status === 'open' || q.status === 'upcoming'
    )
    
    const totalParticipants = allQuinielas.reduce((sum, q) => 
      sum + q.participants.count, 0
    )
    
    const totalRevenue = allQuinielas.reduce((sum, q) => 
      sum + q.prizePool, 0
    )

    setStats({
      totalQuinielas: allQuinielas.length,
      activeQuinielas: activeQuinielas.length,
      totalParticipants,
      totalRevenue
    })
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate()
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const getStatusBadge = (status) => {
    const badges = {
      upcoming: <span className="badge bg-[#0F3D2E] text-[#F8F8F8]">Próxima</span>,
      open: <span className="badge bg-[#0F3D2E] text-[#F8F8F8]">Abierta</span>,
      closed: <span className="badge bg-[#D4AF37] text-[#1A1A1A]">Cerrada</span>,
      in_progress: <span className="badge bg-[#0F3D2E] text-[#F8F8F8]">En Progreso</span>,
      finished: <span className="badge bg-[#BDBDBD] text-[#1A1A1A]">Finalizada</span>,
      cancelled: <span className="badge bg-[#B11217] text-[#F8F8F8]">Cancelada</span>
    }
    return badges[status] || <span className="badge bg-[#BDBDBD] text-[#1A1A1A]">{status}</span>
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-[#1A1A1A] min-h-screen">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#D4AF37] mb-2">Panel de Operador</h1>
          <p className="text-[#BDBDBD]">Gestiona quinielas y monitorea estadísticas</p>
        </div>
        <Link
          to="/operator/create-quiniela"
          className="btn bg-[#0F3D2E] text-[#F8F8F8] hover:bg-[#0A2D1F] inline-flex items-center justify-center gap-2"
        >
          <PlusCircle size={20} />
          Crear Quiniela
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Trophy className="text-[#22C55E]" size={24} />
          </div>
          <p className="text-sm text-[#BDBDBD] mb-1">Total Quinielas</p>
          <p className="text-3xl font-bold text-[#F8F8F8]">{stats.totalQuinielas}</p>
        </div>

        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="text-[#22C55E]" size={24} />
          </div>
          <p className="text-sm text-[#BDBDBD] mb-1">Quinielas Activas</p>
          <p className="text-3xl font-bold text-[#D4AF37]">{stats.activeQuinielas}</p>
        </div>

        <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <Users className="text-[#22C55E]" size={24} />
          </div>
          <p className="text-sm text-[#BDBDBD] mb-1">Total Participantes</p>
          <p className="text-3xl font-bold text-[#F8F8F8]">{stats.totalParticipants}</p>
        </div>

        <div className="bg-[#222222] p-6 rounded-lg border border-[#D4AF37] shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="text-[#D4AF37]" size={24} />
          </div>
          <p className="text-sm text-[#BDBDBD] mb-1">Ingresos Totales</p>
          <p className="text-3xl font-bold text-[#D4AF37]">
            ${stats.totalRevenue.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E] mb-8">
        <h2 className="text-xl font-semibold mb-4 text-[#F8F8F8]">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/operator/create-quiniela"
            className="p-4 border-2 border-dashed border-[#0F3D2E]/30 rounded-lg hover:border-[#0F3D2E] hover:bg-[#0F3D2E]/10 transition-all group"
          >
            <PlusCircle className="text-[#BDBDBD] group-hover:text-[#22C55E] mb-2" size={32} />
            <h3 className="font-semibold text-[#F8F8F8] mb-1">Nueva Quiniela</h3>
            <p className="text-sm text-[#BDBDBD]">Crear una quiniela para la próxima jornada</p>
          </Link>

          <Link
            to="/operator/payments"
            className="p-4 border-2 border-dashed border-[#0F3D2E]/30 rounded-lg hover:border-[#0F3D2E] hover:bg-[#0F3D2E]/10 transition-all group"
          >
            <FileText className="text-[#BDBDBD] group-hover:text-[#22C55E] mb-2" size={32} />
            <h3 className="font-semibold text-[#F8F8F8] mb-1">Validar Pagos</h3>
            <p className="text-sm text-[#BDBDBD]">Revisar comprobantes pendientes</p>
          </Link>

          <Link
            to="/operator/withdrawals"
            className="p-4 border-2 border-dashed border-[#0F3D2E]/30 rounded-lg hover:border-[#0F3D2E] hover:bg-[#0F3D2E]/10 transition-all group"
          >
            <DollarSign className="text-[#BDBDBD] group-hover:text-[#22C55E] mb-2" size={32} />
            <h3 className="font-semibold text-[#F8F8F8] mb-1">Retiros</h3>
            <p className="text-sm text-[#BDBDBD]">Gestionar solicitudes de retiro</p>
          </Link>

          <Link
            to="/operator/usuarios"
            className="p-4 border-2 border-dashed border-[#0F3D2E]/30 rounded-lg hover:border-[#0F3D2E] hover:bg-[#0F3D2E]/10 transition-all group"
          >
            <Users className="text-[#BDBDBD] group-hover:text-[#22C55E] mb-2" size={32} />
            <h3 className="font-semibold text-[#F8F8F8] mb-1">Usuarios</h3>
            <p className="text-sm text-[#BDBDBD]">Ver lista de usuarios registrados</p>
          </Link>
        </div>
      </div>

      {/* Quinielas List */}
      <div className="bg-[#222222] p-6 rounded-lg border border-[#0F3D2E]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-[#F8F8F8]">Mis Quinielas</h2>
          <Link to="/quinielas" className="text-[#22C55E] hover:underline text-sm">
            Ver todas
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader className="animate-spin text-[#22C55E]" size={40} />
          </div>
        ) : quinielas.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="mx-auto mb-4 text-[#BDBDBD]" size={48} />
            <h3 className="text-lg font-semibold text-[#F8F8F8] mb-2">
              No has creado ninguna quiniela
            </h3>
            <p className="text-[#BDBDBD] mb-4">
              Comienza creando tu primera quiniela
            </p>
            <Link
              to="/operator/create-quiniela"
              className="btn bg-[#0F3D2E] text-[#F8F8F8] hover:bg-[#0A2D1F] inline-flex items-center gap-2"
            >
              <PlusCircle size={20} />
              Crear Quiniela
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#22C55E]/20">
              <thead className="bg-[#0F3D2E]/20">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#F8F8F8] uppercase tracking-wider">
                    Quiniela
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#F8F8F8] uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#F8F8F8] uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#F8F8F8] uppercase tracking-wider">
                    Participantes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#F8F8F8] uppercase tracking-wider">
                    Bolsa
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#F8F8F8] uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[#1A1A1A] divide-y divide-[#22C55E]/20">
                {quinielas.slice(0, 10).map((quiniela) => (
                  <tr key={quiniela.id} className="hover:bg-[#222222]/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-[#F8F8F8]">{quiniela.name}</div>
                      <div className="text-sm text-[#BDBDBD]">
                        {quiniela.league} - Jornada {quiniela.jornada}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(quiniela.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#BDBDBD]">
                      {formatDate(quiniela.startDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#F8F8F8]">
                      <div className="flex items-center gap-1">
                        <Users size={16} className="text-[#BDBDBD]" />
                        {quiniela.participants.count}
                        {quiniela.participants.maxLimit && ` / ${quiniela.participants.maxLimit}`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#D4AF37]">
                      ${quiniela.prizePool.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/quinielas/${quiniela.id}`}
                          className="text-[#22C55E] hover:text-[#D4AF37]"
                          title="Ver detalles"
                        >
                          <Eye size={18} />
                        </Link>
                        <Link
                          to={`/operator/quiniela/${quiniela.id}/edit`}
                          className="text-[#22C55E] hover:text-[#D4AF37]"
                          title="Editar"
                        >
                          <Edit size={18} />
                        </Link>
                        <Link
                          to={`/operator/quiniela/${quiniela.id}/results`}
                          className="text-[#D4AF37] hover:text-[#F8F8F8]"
                          title="Gestionar resultados"
                        >
                          <BarChart3 size={18} />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

